import type { QueryClient } from '@tanstack/react-query';
import type { AuthSession } from '@/api/authSession';
import { useUIStore } from '@/store/uiStore';
import { useToastStore } from '@/store/toastStore';
import { useCuttingSizeStore } from '@/store/cuttingSizeStore';

const DRAFT_PREFIX = 'mini_erp_draft:';
function clearPrivateStorage(includeDrafts: boolean) {
  const keys = ['mini_erp_token', 'auth_redirect_to', 'currentRole', 'draft_create_order'];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (includeDrafts && key?.startsWith(DRAFT_PREFIX)) keys.push(key);
  }
  keys.forEach(key => localStorage.removeItem(key));
}

export function bindSessionWorkspace(session: AuthSession, queryClient: QueryClient) {
  let generation = session.getSnapshot().generation;
  const clear = (includeDrafts = true) => {
    void queryClient.cancelQueries();
    queryClient.clear();
    useUIStore.getState().closeAllModals();
    useUIStore.setState({ modalData: null, editingCustomer: null, editingSupplier: null,
      selectedSupplier: null, selectedCustomer: null, selectedCategory: null,
      selectedMaterial: null, selectedCuttingOrder: null, refreshCallbacks: {} });
    useCuttingSizeStore.getState().setSelectedCuttingSize(null);
    useToastStore.getState().clearAllToasts();
    try { clearPrivateStorage(includeDrafts); } catch { /* Memory state is cleared even if storage is disabled. */ }
  };
  clear(false);
  return session.subscribe(() => {
    if (generation !== session.getSnapshot().generation) {
      generation = session.getSnapshot().generation;
      clear();
    }
  });
}

export function createActorDraft(session: AuthSession, name: string) {
  const { generation, user } = session.getSnapshot();
  const key = `${DRAFT_PREFIX}${encodeURIComponent(user?.actor_id || '')}:${name}`;
  const active = () => Boolean(user) && session.getSnapshot().generation === generation &&
    session.getSnapshot().status === 'authenticated' && !session.getSnapshot().user?.must_change_password;
  return {
    read: () => active() ? localStorage.getItem(key) : null,
    write: (value: string) => { if (active()) localStorage.setItem(key, value); },
    clear: () => { if (active()) localStorage.removeItem(key); },
  };
}
